import React, { useState, useEffect, useCallback, useRef } from "react";
import { useHandoffAuth } from "./hooks/useHandoffAuth";
import useTranslator from "../../hooks/useTranslator";
import CaptureScreen from "./components/CaptureScreen";
import GuideScreen from "./components/GuideScreen";
import ReviewScreen from "./components/ReviewScreen";
import { uploadDocument, submitSession } from "./services/mobileCaptureApi";

type PhoneStep =
  | "loading"
  | "guide"
  | "capture_front"
  | "review_front"
  | "capture_back"
  | "review_back"
  | "uploading"
  | "done"
  | "error";

interface MobileCaptureProps {
  sessionToken: string;
}

const MobileCapture: React.FC<MobileCaptureProps> = ({ sessionToken }) => {
  const { handoffToken, sessionInfo, isLoading, error, open } =
    useHandoffAuth(sessionToken);
  const { _t } = useTranslator();

  const [step, setStep] = useState<PhoneStep>("loading");
  const [frontImage, setFrontImage] = useState<File | Blob | null>(null);
  const [backImage, setBackImage] = useState<File | Blob | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const isSubmitting = useRef(false);

  useEffect(() => {
    open();
  }, [open]);

  useEffect(() => {
    if (handoffToken && sessionInfo) {
      if (
        sessionInfo.status === "PHONE_OPENED" ||
        sessionInfo.status === "PENDING"
      ) {
        setStep("guide");
      }
    }
  }, [handoffToken, sessionInfo]);

  useEffect(() => {
    if (error) {
      setStep("error");
    }
  }, [error]);

  const handleCaptureFront = useCallback((imageBlob: File | Blob) => {
    setFrontImage(imageBlob);
    setStep("review_front");
  }, []);

  const handleCaptureBack = useCallback((imageBlob: File | Blob) => {
    setBackImage(imageBlob);
    setStep("review_back");
  }, []);

  const handleConfirmFront = useCallback(async () => {
    if (!frontImage || isSubmitting.current) return;
    isSubmitting.current = true;
    setStep("uploading");
    try {
      await uploadDocument(sessionToken, frontImage as File, "front");
      if (sessionInfo?.requires_back) {
        setUploadError(null);
        setStep("guide");
      } else {
        await submitSession(sessionToken);
        setStep("done");
      }
    } catch (err: any) {
      setUploadError(err?.message || _t("mobileCaptureUploadError"));
      setStep("review_front");
    } finally {
      isSubmitting.current = false;
    }
  }, [frontImage, sessionToken, sessionInfo, _t]);

  const handleConfirmBack = useCallback(async () => {
    if (!backImage || isSubmitting.current) return;
    isSubmitting.current = true;
    setStep("uploading");
    try {
      await uploadDocument(sessionToken, backImage as File, "back");
      await submitSession(sessionToken);
      setStep("done");
    } catch (err: any) {
      setUploadError(err?.message || _t("mobileCaptureUploadError"));
      setStep("review_back");
    } finally {
      isSubmitting.current = false;
    }
  }, [backImage, sessionToken, _t]);

  const handleRetakeFront = useCallback(() => {
    setFrontImage(null);
    setUploadError(null);
    setStep("capture_front");
  }, []);

  const handleRetakeBack = useCallback(() => {
    setBackImage(null);
    setUploadError(null);
    setStep("capture_back");
  }, []);

  if (isLoading) {
    return (
      <div className="mobile-capture-screen mobile-capture-loading">
        <div className="mobile-capture-spinner" />
        <p>{_t("mobileCaptureOpening")}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mobile-capture-screen mobile-capture-error">
        <div className="mobile-capture-error-icon">!</div>
        <h3>{_t("mobileCaptureErrorTitle")}</h3>
        <p>{error}</p>
      </div>
    );
  }

  if (step === "guide") {
    const frontCaptured = Boolean(frontImage);
    return (
      <GuideScreen
        docType={sessionInfo?.doc_type}
        requiresBack={Boolean(sessionInfo?.requires_back)}
        frontCaptured={frontCaptured}
        onStart={() =>
          setStep(frontCaptured ? "capture_back" : "capture_front")
        }
      />
    );
  }

  if (step === "capture_front") {
    return (
      <CaptureScreen
        side="front"
        onCapture={handleCaptureFront}
        onCancel={() => setStep("guide")}
      />
    );
  }

  if (step === "capture_back") {
    return (
      <CaptureScreen
        side="back"
        onCapture={handleCaptureBack}
        onCancel={() => setStep("guide")}
      />
    );
  }

  if (step === "review_front" && frontImage) {
    return (
      <ReviewScreen
        image={frontImage}
        side="front"
        error={uploadError}
        onConfirm={handleConfirmFront}
        onRetake={handleRetakeFront}
      />
    );
  }

  if (step === "review_back" && backImage) {
    return (
      <ReviewScreen
        image={backImage}
        side="back"
        error={uploadError}
        onConfirm={handleConfirmBack}
        onRetake={handleRetakeBack}
      />
    );
  }

  if (step === "uploading") {
    const translate = (key: string, fallback: string) => {
      const translated = _t(key, fallback);
      return translated === key ? fallback : translated;
    };

    return (
      <div className="mobile-capture-screen mobile-capture-uploading">
        <div className="mobile-capture-uploading-content">
          <div className="mobile-capture-spinner" aria-hidden="true" />
          <h3>{translate("uploadingThePhoto", "Uploading the photo")}</h3>
          <p>
            {translate("doNotCloseThisScreen", "Do not close this screen")}
          </p>
        </div>
      </div>
    );
  }

  if (step === "done") {
    const translate = (key: string, fallback: string) => {
      const translated = _t(key, fallback);
      return translated === key ? fallback : translated;
    };

    return (
      <div className="mobile-capture-screen mobile-capture-done">
        <div className="mobile-capture-done-content">
          <div className="mobile-capture-done-icon" aria-hidden="true">
            ✓
          </div>
          <h3>{translate("photosSent", "Photos sent")}</h3>
          <p>
            {translate(
              "youCanCloseThisScreen",
              "You can now close this screen."
            )}
            <br />
            {translate(
              "weContinueOnYourComputer",
              "We'll continue on your computer."
            )}
          </p>
        </div>
      </div>
    );
  }

  return null;
};

export default MobileCapture;
